/**
 * Freeze diagnostic state and controls.
 *
 * Freezing clones the camera and installs the clone as the map's
 * selection camera. Navigation then moves the rendered view while tile
 * descent continues to evaluate the frozen viewpoint. The keyboard
 * sub-mode only controls this state; leaving that sub-mode does not
 * unfreeze the map.
 */

import type Map from '../map/map';
import type MapCamera from '../map/camera';


/**
 * Owns freeze-state DOM, draw-state wiring, and frustum capture. Every
 * operation takes the typed map; one without a loaded map is ignored.
 */
export class FreezeMode {

    active = false;
    drawFrustum = false;
    frustumApex: number[] | null = null;
    frustumBase: number[][] | null = null;

    private controlsActive_ = false;
    private navDefinition_: MapCamera.Definition | null = null;
    private controlsEl_: HTMLElement | null = null;
    private freezeBtn_: HTMLButtonElement | null = null;
    private frustumBtn_: HTMLButtonElement | null = null;
    private resetBtn_: HTMLButtonElement | null = null;

    /** Freeze terrain selection at the live camera. */
    freeze(map: Map): void {

        if (!map.legacyMap) return;

        const camera = map.camera;

        this.navDefinition_ = camera.definition;
        map.freezeSelection(camera.clone());

        this.active = true;
        this.ensureControls(map);
        this.updateControls();
    }

    /** Disable frozen selection and remove its persistent controls. */
    unfreeze(map: Map): void {

        map.unfreezeSelection();

        this.active = false;
        this.drawFrustum = false;
        this.frustumApex = null;
        this.frustumBase = null;
        this.navDefinition_ = null;
        this.updateControls();
        this.removeControlsIfIdle();
    }

    /** Toggle frozen selection at the current live camera. */
    toggleFrozen(map: Map): void {

        if (!map.legacyMap) return;

        if (this.active) {

            this.unfreeze(map);

        } else {

            this.freeze(map);
        }
    }

    /** Restore the live camera to the frozen definition. */
    resetView(map: Map): void {

        if (!map.legacyMap || !this.navDefinition_) return;

        map.camera.set(this.navDefinition_);
    }

    /** Toggle the frozen-camera frustum pyramid. */
    toggleFrustum(map: Map): void {

        if (!this.active || !map.legacyMap) return;

        this.drawFrustum = !this.drawFrustum;
        if (this.drawFrustum) {

            this.captureFrustum(map);
        } else {

            this.frustumApex = null;
            this.frustumBase = null;
        }

        this.updateControls();
        map.legacyMap.markDirty();
    }

    /** Show or hide the freeze command strip. */
    setControlsActive(map: Map, active: boolean): void {

        this.controlsActive_ = active;

        if (active && map.legacyMap) {

            this.ensureControls(map);
            this.updateControls();

        } else {

            this.removeControlsIfIdle();
        }
    }

    /**
     * Compute the frozen-camera pyramid from the hitmap's farthest depth.
     */
    captureFrustum(map: Map): void {

        if (!this.active) return;

        const renderer = map.renderer;
        const selection = map.selectionCamera;
        const [w, h] = renderer.getCanvasSize();

        // the frozen camera's own depth, read straight off the draw
        const maxDepth = map.withSelectionCamera(() => {

            map.updateDepthHitmap();
            return renderer.getMaxFiniteDepth(3, true);
        });
        const depth = maxDepth !== null
            ? maxDepth * 1.1
            : this.referenceFrameExtent(map);

        const apex = selection.geometry.eye.slice();
        const corners: [number, number][] = [
            [0, 0],
            [w, 0],
            [w, h],
            [0, h],
        ];

        const base = map.withSelectionCamera(() =>
            corners.map(([x, y]) => {

                const ray = renderer.getScreenRay(x, y, 'layout');
                return [
                    apex[0] + ray[0] * depth,
                    apex[1] + ray[1] * depth,
                    apex[2] + ray[2] * depth,
                ];
            }));

        this.frustumApex = apex;
        this.frustumBase = base;
    }

    private referenceFrameExtent(map: Map): number {

        const ext = map.legacyMap?.referenceFrame?.division?.extents;
        if (!ext) return 0;

        const dx = ext.ur[0] - ext.ll[0];
        const dy = ext.ur[1] - ext.ll[1];
        const dz = ext.ur[2] - ext.ll[2];

        return Math.max(dx, dy, dz);
    }

    private ensureControls(map: Map): void {

        if (this.controlsEl_) return;

        const controls = document.createElement('div');
        controls.className = 'vts-freeze-controls';
        controls.addEventListener('pointerdown', (event) => {

            event.stopPropagation();
        });
        controls.addEventListener('click', (event) => {

            event.stopPropagation();
        });

        this.freezeBtn_ = this.createButton('Freeze', () => {

            this.toggleFrozen(map);
        });
        controls.appendChild(this.freezeBtn_);

        this.frustumBtn_ = this.createButton('Show frustum', () => {

            this.toggleFrustum(map);
        });
        controls.appendChild(this.frustumBtn_);

        this.resetBtn_ = this.createButton('Reset view', () => {

            this.resetView(map);
        });
        controls.appendChild(this.resetBtn_);

        document.body.appendChild(controls);
        this.controlsEl_ = controls;
        this.updateControls();
    }

    private createButton(
        label: string,
        onClick: () => void,
    ): HTMLButtonElement {

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = label;
        btn.addEventListener('click', (event) => {

            event.preventDefault();
            event.stopPropagation();
            onClick();
            btn.blur();
        });
        return btn;
    }

    private updateControls(): void {

        if (!this.freezeBtn_ || !this.frustumBtn_ || !this.resetBtn_) return;

        this.freezeBtn_.textContent = this.active ? 'Unfreeze' : 'Freeze';
        this.frustumBtn_.textContent = this.drawFrustum
            ? 'Hide frustum'
            : 'Show frustum';
        this.frustumBtn_.disabled = !this.active;
        this.resetBtn_.disabled = !this.active;
    }

    private removeControlsIfIdle(): void {

        if (this.active || this.controlsActive_) return;

        this.controlsEl_?.remove();
        this.controlsEl_ = null;
        this.freezeBtn_ = null;
        this.frustumBtn_ = null;
        this.resetBtn_ = null;
    }
}
